import { useMemo, useCallback } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, Label, Button, FormGroup, Input } from 'reactstrap';

const selectStyles = {
  menuPortal: base => ({ ...base, zIndex: 99999 }),
  menu: base => ({ ...base, zIndex: 99999 }),
  control: base => ({ ...base, zIndex: 1 }),
};

const PropertySelector = ({
  propertyList = [],
  categories = [],
  selectedFilters,
  onPropertySelectionChange,
  onAddClick,
  isLoading = false,
}) => {
  const { property_id, building_name, category, search } = selectedFilters;

  const currentProperty = useMemo(
    () => propertyList.find(p => p._id === property_id) ?? null,
    [propertyList, property_id]
  );

  const buildings = useMemo(() => {
    if (!currentProperty?.building) return [];
    return currentProperty.building
      .filter(b => b?.buildingName)
      .map(b => b.buildingName);
  }, [currentProperty]);

  const hasBuildings = buildings.length > 0;
  const hasMultipleBuildings = buildings.length > 1;

  const propertyOptions = useMemo(
    () =>
      propertyList.map(p => ({
        value: p._id,
        label: `${p.title || 'Unnamed'}, ${p.location || 'No location'}`,
      })),
    [propertyList]
  );

  const buildingOptions = useMemo(
    () => buildings.map(b => ({ value: b, label: b })),
    [buildings]
  );

  const categoryOptions = useMemo(
    () => categories.map(c => ({ value: c, label: c.toUpperCase() })),
    [categories]
  );

  const selectedPropertyOption = useMemo(
    () => propertyOptions.find(o => o.value === property_id) ?? null,
    [propertyOptions, property_id]
  );

  const selectedBuildingOption = useMemo(
    () => buildingOptions.find(o => o.value === building_name) ?? null,
    [buildingOptions, building_name]
  );

  const selectedCategoryOption = useMemo(
    () => categoryOptions.find(o => o.value === category) ?? null,
    [categoryOptions, category]
  );

  const isAddEnabled = useMemo(() => {
    if (!property_id) return false;                    
    if (hasBuildings) {
      return !!building_name;              
    }
    return true;
  }, [property_id, building_name, hasBuildings]);

  const handlePropertyChange = useCallback(
    selected => {
      const newId = selected?.value || '';
      const prop = propertyList.find(p => p._id === newId);

      const newBuildings = prop?.building
        ?.filter(b => b?.buildingName)
        ?.map(b => b.buildingName) ?? [];

      const autoSelectBuilding = newBuildings.length === 1 ? newBuildings[0] : '';

      onPropertySelectionChange(prev => ({
        ...prev,
        property_id: newId,
        building_name: autoSelectBuilding,
      }));
    },
    [propertyList, onPropertySelectionChange]
  );

  const handleBuildingChange = useCallback(
    selected =>
      onPropertySelectionChange(prev => ({
        ...prev,
        building_name: selected?.value || '',
      })),
    [onPropertySelectionChange]
  );

  const handleCategoryChange = useCallback(
    selected =>
      onPropertySelectionChange(prev => ({
        ...prev,
        category: selected?.value || '',
      })),
    [onPropertySelectionChange]
  );

  const handleSearchChange = useCallback(
    e =>
      onPropertySelectionChange(prev => ({
        ...prev,
        search: e.target.value,
      })),
    [onPropertySelectionChange]
  );

  return (
    <Form className="mb-4">
      <Row className="align-items-end g-3">
        <Col md={3}>
          <FormGroup>
            <Label>Property *</Label>
            <Select
              value={selectedPropertyOption}
              onChange={handlePropertyChange}
              options={propertyOptions}
              isClearable
              isDisabled={isLoading}
              placeholder="Select property..."
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FormGroup>
        </Col>

        {hasBuildings && (
          <Col md={2}>
            <FormGroup>
              <Label>
                Building {hasMultipleBuildings ? '*' : '(auto)'}
              </Label>
              <Select
                value={selectedBuildingOption}
                onChange={handleBuildingChange}
                options={buildingOptions}
                isClearable={hasMultipleBuildings}
                isDisabled={!hasMultipleBuildings || isLoading}
                placeholder={hasMultipleBuildings ? "Select building..." : ""}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </FormGroup>
          </Col>
        )}

        <Col md={hasBuildings ? 2 : 3}>
          <FormGroup>
            <Label>Category</Label>
            <Select
              value={selectedCategoryOption}
              onChange={handleCategoryChange}
              options={categoryOptions}
              isClearable
              isDisabled={isLoading}
              placeholder="All categories"
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FormGroup>
        </Col>

        <Col md={hasBuildings ? 3 : 4}>
          <FormGroup>
            <Label>Search keyword</Label>
            <Input
              value={search || ''}
              onChange={handleSearchChange}
              placeholder="Filter by name, address..."
              disabled={isLoading}
            />
          </FormGroup>
        </Col>

        <Col md={1}>
          <Button
            color="primary"
            onClick={onAddClick}
            className="w-100"
            disabled={isLoading || !isAddEnabled}
            title={
              !isAddEnabled
                ? "Select a property" + (hasBuildings ? " and building" : "")
                : ""
            }
          >
            <FeatherIcon icon="plus-circle" size={18} className="me-2" />
            Add
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export { PropertySelector };